import { fetchQuery } from 'react-relay';

import { __INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__ as platformEnv } from '@atlassian/relay-environment-provider/environment';

import { expValEquals } from '@confluence/feature-experiments';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import { buildInvitedAppShortcutsRecommendationsVariables } from './PlatformNavigation/AppShortcuts/invitedAppShortcutsRecommendationsVariables.ts';
import InvitedAppShortcutsRecommendationsQuery from './PlatformNavigation/AppShortcuts/__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

/**
 * SSR preloader for the Invited App Shortcuts GRS recommendations query
 * Mirrors `preloadPlatformNavigationQuery`.
 *
 * Registered in `preloadSideNavigation` / `preloadAccessLinkRoute` alongside
 * `preloadPlatformNavigationQuery`, so it inherits the same route-eligibility.
 *
 * Variables MUST be byte-for-byte identical to the render-time consumer in
 * `InvitedAppShortcutsRecommendations.tsx` (tenantId=cloudId, product=confluence)
 * or the client's store-or-network lookup misses and it refetches. Both sides
 * build them from `buildInvitedAppShortcutsRecommendationsVariables`, which is
 * what actually enforces this.
 */
export const preloadInvitedAppShortcutsRecommendationsQuery = async (): Promise<void> => {
	const { cloudId, userId } = getPreloaderFnContext();

	// Both are required by the recommendations renderer.
	if (!cloudId || !userId) {
		return;
	}

	// Shared builder — keeps these byte-identical to the render-time consumer.
	const variables = buildInvitedAppShortcutsRecommendationsVariables(cloudId);

	// On the server (Bifrost SSR), await the AGG fetch so the Relay store is populated before
	// render. The client reads the serialised store synchronously via store-or-network — no second
	// network request. Wrapped in `slowQueryGuard` so a slow/hanging GRS call can't stall the
	// side-nav SSR preload: on timeout it resolves and we fall through to the client store-or-network
	// + fail-open default menu. Degrade gracefully if platformEnv lacks auth in the Bifrost context.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		try {
			await slowQueryGuard(
				'preloadInvitedAppShortcutsRecommendationsQuery',
				() =>
					fetchQuery(
						expValEquals('confluence_gsn_shared_relay_environment', 'isEnabled', true)
							? getRelayEnvironment()
							: platformEnv,
						InvitedAppShortcutsRecommendationsQuery,
						variables,
					).toPromise(),
				SLOW_QUERY_DEFAULT_TIMEOUT,
			);
		} catch (e) {
			// Degrade gracefully: the client falls through to store-or-network + the fail-open default
			// menu. Log a sanitized message + operation name only — never the raw error object, which
			// could carry backend/tenant detail into SSR logs.
			// eslint-disable-next-line no-console
			console.warn(
				`[InvitedAppShortcutsRecommendations preloader] InvitedAppShortcutsRecommendationsQuery failed in SSR: ${
					e instanceof Error ? e.message : 'unknown error'
				}`,
			);
		}
	}
};
